import React from 'react';
import { Typography } from 'antd';

const { Paragraph } = Typography;

// Render **bold** segments as accented <strong> (text nodes only — no HTML injection).
const BOLD_RE = /(\*\*[^*]+\*\*)/g;
const BOLD_ONE = /^\*\*[^*]+\*\*$/;
// A line that is nothing but bold text is a section title, e.g. "**Next steps**".
const HEADING_RE = /^\*\*([^*]+?)\*\*:?$/;
const BULLET_RE = /^[-*]\s+/;

const renderInline = (text: string, accent: string) =>
  text.split(BOLD_RE).map((part, i) =>
    (BOLD_ONE).test(part)
      ? <strong key={i} style={{ color: accent }}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );

type Block =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'bullets'; items: string[] };

// Group the model's light markup into blocks, keeping the order it was written in.
const toBlocks = (text: string): Block[] => {
  const blocks: Block[] = [];

  text
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)
    .forEach((line) => {
      const heading = HEADING_RE.exec(line);
      const last = blocks[blocks.length - 1];

      if (heading) {
        blocks.push({ kind: 'heading', text: heading[1].trim() });
      } else if (BULLET_RE.test(line)) {
        const item = line.replace(BULLET_RE, '');

        if (last && last.kind === 'bullets') {
          last.items.push(item);
        } else {
          blocks.push({ kind: 'bullets', items: [item] });
        }
      } else {
        blocks.push({ kind: 'paragraph', text: line });
      }
    });

  return blocks;
};

// Turn the model's light markup (summary line, bold section titles, "- " bullets) into clean UI.
export const renderExplanation = (text: string, accent: string) => (
  <>
    {toBlocks(text).map((block, i) => {
      if (block.kind === 'heading') {
        return <div key={i} className='ai-summary-heading' style={{ color: accent }}>{block.text}</div>;
      }

      if (block.kind === 'bullets') {
        return (
          <ul key={i} style={{ margin: '0 0 4px', paddingLeft: 18 }}>
            {block.items.map((item, j) => (
              <li key={j} style={{ marginBottom: 4 }}>{renderInline(item, accent)}</li>
            ))}
          </ul>
        );
      }

      return (
        <Paragraph key={i} style={{ marginBottom: 8 }}>
          {renderInline(block.text, accent)}
        </Paragraph>
      );
    })}
  </>
);
